import { CommonModule } from '@angular/common'
import { Component, Input, ViewChild } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { PermissionStore } from '@engineering11/access-web'
import { E11DividerComponent } from '@engineering11/ui-lib/e11-divider'
import { E11DynamicFormModule } from '@engineering11/ui-lib/e11-form-builder'
import { ISelectOption } from '@engineering11/ui-lib/e11-select'
import { E11ShowHideWrapperComponent } from '@engineering11/ui-lib/e11-show-hide'
import { LetDirective } from '@ngrx/component'
import { TranslateModule } from '@ngx-translate/core'
import { combineLatest, map } from 'rxjs'
import { APPLICATION_STATE, ConfigStore, CultureIndexPermissions, SharedLibModule } from 'shared-lib'
import { IAutomationFormConfig, swimlaneForm } from '../automation-form-message'
import { AutomationSwimlaneChangeService } from '../automation-swimlane-change.service'
import { ICultureIndexFormData } from '../culture-index.model'
import { AutomationFormHeaderComponent } from '../partials/automation-form-header.component'
import { AutomationHeaderInfoComponent } from '../partials/automation-header-info.component'
import { AutomationReminderMessagesComponent } from '../partials/automation-reminder-messages.component'
import { AutomationTypeDisplayNames } from '../partials/automation-upgrade-prompt.component'
import { deepNormalizeNullishStrings } from '@engineering11/utility'

@Component({
  selector: 'automation-culture-index-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    E11DynamicFormModule,
    E11ShowHideWrapperComponent,
    TranslateModule,
    AutomationFormHeaderComponent,
    AutomationReminderMessagesComponent,
    SharedLibModule,
    LetDirective,
    AutomationHeaderInfoComponent,
    E11DividerComponent,
  ],
  template: `
    <ng-container *ngrxLet="canManageCultureIndex$ as canManageCultureIndex">
      <ng-container *ngIf="canManageCultureIndex">
        <div>
          <automation-form-header
            [sectionTitle]="'Culture Index Survey' | translate"
            [showEnableSwitch]="showEnableSwitch"
            [isCollapsed]="isCollapsed"
            [isEnabled]="_initialData?.enabled ?? false"
            (enabledChanged)="isEnabled = $event"
            (isCollapsedChanged)="isCollapsed = $event"
            [ottoHelperText]="
              $any(reminderMessagesRef?.initialMessageRef?.value)?.newSwimlane || _initialData?.newSwimlane
                ? 'You currently have an automated swimlane change from Screened to ' +
                  ($any(reminderMessagesRef?.initialMessageRef?.value)?.newSwimlane || _initialData?.newSwimlane | applicationState) +
                  ' once a candidate completes a Request for More task.'
                : 'You do not currently have a swimlane change configured for this automation task.'
            "
          ></automation-form-header>
          <div class="e11-flex e11-gap-4 e11-items-center e11-mb-6">
            <img src="assets/images/logo/cx-logo.png" alt="culture index logo" class="e11-w-16 e11-h-16" />
            <span class="e11-mr-1">
              {{
                'A Culture Index Survey (TM) can be requested for all submitted applications. Choose when to send the applicant a request to take the survey. An application will not get past the “Applied” status until the survey is completed. If this automated request is off you can still manually send a request to take the survey to the candidate.'
                  | translate
              }}</span
            >
          </div>

          <e11-show-hide-wrapper [isCollapsed]="isCollapsed">
            <automation-reminder-messages
              #reminderMessagesRef
              [automationTypeDisplayName]="automationTypeDisplayName"
              [loading]="loading"
              [showResetButton]="showResetButton"
              [formConfig]="formConfig"
              [showImmediateSwitch]="true"
              [immediateValue]="_initialData?.immediate"
              [initialMessageConfig]="{
                data: _initialData?.initialMessage,
                fieldName: 'cultureIndexMessage',
                showEnableSwitch: false,
                footNoteText: 'Message to applicants requesting they take the survey.',
                extraFieldGroups: [buildExtraFieldGroups()],
                delayTimeHeaderText: 'How long to wait before sending a survey request?',
              }"
              [followUpMessageConfig]="{
                data: _initialData?.followUp,
                fieldName: 'cultureIndexFollowUpMessage',
                showEnableSwitch: showEnableSwitch,
                footNoteText: 'Sent as a reminder if the candidate does not respond in the chosen time.'
              }"
              [warningMessageConfig]="{
                data: _initialData?.warning,
                fieldName: 'cultureIndexWarningMessage',
                showEnableSwitch: showEnableSwitch,
                footNoteText: 'Sent as a final reminder if the candidate does not respond to previous requests.'
              }"
            >
              <automation-header-info
                ngProjectAs="initial-message-extra-field-groups"
                [titleText]="'Where to place candidate on completion?' | translate"
              ></automation-header-info>
            </automation-reminder-messages>
          </e11-show-hide-wrapper>
        </div>
        <e11-divider [margin]="1"></e11-divider>
      </ng-container>
    </ng-container>
  `,
})
export class AutomationCultureIndexFormComponent {
  @ViewChild('reminderMessagesRef') reminderMessagesRef!: AutomationReminderMessagesComponent

  @Input() loading = false
  @Input() showEnableSwitch = false
  @Input() showResetButton = false
  @Input() formConfig: IAutomationFormConfig | undefined
  @Input() set initialData(data: ICultureIndexFormData | undefined) {
    this._initialData = data
    this.isEnabled = data?.enabled ?? false
  }
  _initialData?: ICultureIndexFormData
  @Input({ required: true }) swimlaneOptions: ISelectOption<APPLICATION_STATE | null>[] = []

  automationTypeDisplayName = AutomationTypeDisplayNames.CultureIndexSurvey
  isCollapsed = false
  features$ = this.configStore.features$
  canManageCultureIndex$ = combineLatest([this.features$, this.permissionStore.hasPermission(CultureIndexPermissions.ManageCultureIndex)]).pipe(
    map(([features, hasPermission]) => features?.cultureIndex && hasPermission)
  )
  isEnabled = false
  constructor(
    private configStore: ConfigStore,
    private permissionStore: PermissionStore,
    public swimlaneChangeService: AutomationSwimlaneChangeService
  ) {}

  buildExtraFieldGroups() {
    return swimlaneForm(this.swimlaneOptions, this._initialData?.newSwimlane ?? undefined)
  }

  get value(): ICultureIndexFormData | undefined {
    if (!this.reminderMessagesRef) return undefined
    const reminderMessages = deepNormalizeNullishStrings(this.reminderMessagesRef.getValue())
    return {
      enabled: this.isEnabled,
      immediate: reminderMessages?.immediate ?? false,
      senderId: reminderMessages?.initialMessage.senderId,
      newSwimlane: (reminderMessages?.initialMessage as unknown as { newSwimlane: APPLICATION_STATE | null }).newSwimlane,
      initialMessage: { ...reminderMessages?.initialMessage, enabled: this.isEnabled },
      followUp: reminderMessages?.followUp,
      warning: reminderMessages?.warning,
    }
  }

  validate(): boolean {
    if (!this.reminderMessagesRef) return true
    return this.reminderMessagesRef.validate()
  }
}
